import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown, ArrowUp, Check, Keyboard, MousePointer, Play, RotateCw, X } from 'lucide-react';
import { API_BASE } from '../config';
import './BrowserTakeover.css';

export default function BrowserTakeover({ jobId, approval }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(false);
  const [image, setImage] = useState('');
  const [viewState, setViewState] = useState('loading');
  const [url, setUrl] = useState(approval.url);
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [done, setDone] = useState(false);
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [inputBusy, setInputBusy] = useState(false);
  const [key, setKey] = useState('Tab');
  const sequence = useRef(0);
  const pressed = useRef(false);
  const outbox = useRef([]);
  const sending = useRef(false);
  const alive = useRef(false);
  const activeIntent = useRef(false);
  const controlRevision = useRef(0);
  const dialog = useRef(null);
  const lastMove = useRef(0);
  const expired = Date.now() > approval.expires_at*1000;
  const ended = Boolean(approval.state && approval.state !== 'pending');
  const endpoint = `${API_BASE}/v1/chat/jobs/${jobId}/browser-human/${approval.pending_id}`;
  const request = async (operation, event = {}) => {
    const response = await fetch(endpoint, { method: 'POST', credentials: 'include', cache: 'no-store',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ operation, event }) });
    const result = await response.json();
    if (!response.ok) {
      const error = new Error(typeof result.detail === 'string' ? result.detail : '接管操作未完成，请核实页面状态；操作不会自动重试。');
      error.status = response.status;
      throw error;
    }
    return result;
  };
  useEffect(() => {
    alive.current = open && !done && !expired && !ended;
    if (!alive.current) { setImage(''); setText(''); setActive(false); activeIntent.current = false; return undefined; }
    const previous = document.activeElement;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.focus();
    let timer;
    const frame = async () => {
      try {
        const revision = controlRevision.current;
        const result = await request('frame', { heartbeat: activeIntent.current && document.visibilityState === 'visible' });
        if (alive.current) {
          if (result.finished) {
            activeIntent.current = false; setImage(''); setText(''); setActive(false); setVerified(true); setDone(true); setOpen(false);
            return;
          }
          setImage(`data:image/jpeg;base64,${result.image}`);
          setViewState(result.view_state || 'ready');
          setUrl(result.url);
          if (revision === controlRevision.current) {
            setActive(Boolean(result.active));
            if (!result.active) activeIntent.current = false;
          }
          sequence.current = Math.max(sequence.current, result.seq || 0);
        }
      } catch (err) {
        if (alive.current) {
          if ([404, 410].includes(err.status)) { setDone(true); setOpen(false); setImage(''); setText(''); }
          else setError(err.message);
        }
      }
      if (alive.current) timer = window.setTimeout(frame, 1000);
    };
    frame();
    return () => {
      alive.current = false;
      window.clearTimeout(timer);
      outbox.current = [];
      pressed.current = false;
      activeIntent.current = false;
      document.body.style.overflow = oldOverflow;
      previous?.focus?.();
    };
    // The lease identity is fixed for this keyed component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, done, expired, ended]);
  const finish = async operation => {
    if (operation === 'finish' && sending.current) { setError('请等待当前操作返回后再继续任务。'); return; }
    setBusy(true);
    try {
      await request(operation);
      activeIntent.current = false; setDone(true); setOpen(false); setActive(false); setImage(''); setText('');
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const start = async () => {
    setBusy(true); setError('');
    controlRevision.current += 1;
    try { await request('start', { reload: approval.requires_refresh === true }); controlRevision.current += 1; activeIntent.current = true; setActive(true); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const send = event => {
    if (!active || !alive.current || busy) return;
    const item = { ...event, seq: ++sequence.current };
    const last = outbox.current[outbox.current.length-1];
    if (item.type === 'move' && last?.type === 'move') outbox.current[outbox.current.length-1] = item;
    else outbox.current.push(item);
    if (sending.current) return;
    sending.current = true;
    setInputBusy(true);
    const drain = async () => {
      try {
        while (outbox.current.length && alive.current) await request('event', outbox.current.shift());
      } catch (err) { outbox.current = []; pressed.current = false; activeIntent.current = false; setError(err.message); setActive(false); }
      finally { sending.current = false; setInputBusy(false); }
    };
    drain();
  };
  const point = (event, clamp = false) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const scale = Math.min(rect.width/1280, rect.height/800);
    const x = (event.clientX-rect.left-(rect.width-1280*scale)/2)/scale;
    const y = (event.clientY-rect.top-(rect.height-800*scale)/2)/scale;
    if (!clamp && (x<0 || x>=1280 || y<0 || y>=800)) return null;
    return { x: Math.max(0, Math.min(1279, x)), y: Math.max(0, Math.min(799, y)) };
  };
  const trapFocus = event => {
    if (event.key === 'Escape') { event.preventDefault(); finish('cancel'); }
    if (event.key !== 'Tab') return;
    const buttons = [...dialog.current.querySelectorAll('button:not(:disabled),input:not(:disabled),select:not(:disabled)')];
    const first = buttons[0], last = buttons[buttons.length-1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  return <div className="browser-approval">
    <div className="browser-approval-heading"><MousePointer size={16} /><strong>需要你接管网页</strong></div>
    <div className="browser-approval-url">{approval.url}</div>
    {done || expired || ended ? <span>{verified || (approval.requires_refresh && approval.state === 'submitted') ? '验证已完成，Agent 正在继续任务。' : '接管已结束或过期。'}</span> : <button type="button" className="takeover-open" onClick={() => setOpen(true)}><MousePointer size={14} />打开网页接管</button>}
    {open && !done && !expired && !ended && createPortal(<div className="takeover-backdrop">
      <section className="takeover-dialog" role="dialog" aria-modal="true" aria-label="人工接管浏览器" tabIndex={-1} ref={dialog} onKeyDown={trapFocus}>
        <header><div><strong>人工接管</strong><div className="browser-approval-url">{url}</div></div>
          <button type="button" title="取消接管" aria-label="取消接管" disabled={busy} onClick={() => finish('cancel')}><X size={18} /></button></header>
        {!active && <div className="takeover-consent"><p>{approval.summary ? `${approval.summary}。` : ''}接管期间 Agent 暂停。开始后，你的点击和输入会直接操作当前网站，并允许网页正常提交数据，请确认网站地址后操作。</p>
          <button type="button" disabled={busy || !image} onClick={start}><Play size={15} />{approval.requires_refresh ? '加载验证页面' : '开始接管'}</button></div>}
        <div className={`takeover-view ${active ? 'is-controlling' : ''}`}>
          {image && viewState === 'ready' ? <img src={image} alt="当前任务的私有浏览器画面" draggable={false}
            onPointerDown={event => { if (!active) return; const p=point(event); if (!p) return; event.preventDefault(); event.currentTarget.setPointerCapture(event.pointerId); pressed.current = true; send({ type: 'down', ...p }); }}
            onPointerMove={event => { if (pressed.current && Date.now()-lastMove.current > 60) { lastMove.current = Date.now(); send({ type: 'move', ...point(event, true) }); } }}
            onPointerUp={event => { if (!pressed.current) return; pressed.current = false; send({ type: 'up', ...point(event, true) }); }}
            onPointerCancel={event => { pressed.current = false; send({ type: 'up', ...point(event, true) }); }} /> : <div className="takeover-loading" role="status">{!active && approval.requires_refresh ? '网站验证尚未加载，等待你允许加载。' : '网页尚未显示有效内容；可以刷新重试或取消，不代表验证已通过。'}</div>}
        </div>
        <div className="takeover-tools">
          <button type="button" title="刷新当前网页" aria-label="刷新当前网页" disabled={!active || inputBusy} onClick={() => send({ type: 'reload' })}><RotateCw size={16} /></button>
          <button type="button" title="向上滚动" aria-label="向上滚动" disabled={!active} onClick={() => send({ type: 'scroll', dy: -500 })}><ArrowUp size={16} /></button>
          <button type="button" title="向下滚动" aria-label="向下滚动" disabled={!active} onClick={() => send({ type: 'scroll', dy: 500 })}><ArrowDown size={16} /></button>
          <select aria-label="网页按键" disabled={!active} value={key} onChange={event => setKey(event.target.value)}>{['Tab', 'Shift+Tab', 'Enter', 'Backspace', 'Delete', 'Escape', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Control+A'].map(value => <option key={value}>{value}</option>)}</select>
          <button type="button" title="发送按键" aria-label="发送按键" disabled={!active} onClick={() => send({ type: 'key', key })}><Keyboard size={16} /></button>
          <form onSubmit={event => { event.preventDefault(); if (text) { send({ type: 'text', text }); setText(''); } }}>
            <input type="password" aria-label="私密输入到网页" autoComplete="off" disabled={!active} maxLength={2000} value={text} onChange={event => setText(event.target.value)} />
            <button type="submit" disabled={!active || !text}><Keyboard size={14} />输入到网页</button>
          </form>
        </div>
        {error && <p className="takeover-error" role="alert">{error}</p>}
        <footer><span>画面与输入仅供本任务所属用户使用，不写入聊天记录。完成验证后再继续任务。</span>
          <button type="button" disabled={busy || inputBusy} onClick={() => finish('finish')}><Check size={15} />完成，继续任务</button></footer>
      </section>
    </div>, document.body)}
  </div>;
}
